import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    ExclamationTriangleIcon,
    ClipboardDocumentListIcon,
    FunnelIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getAuditLogs, getActionCategories, getAuditStatuses } from "../api/auditApi";
import type { AuditLogsQueryParams, AuditStatus } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtDate = (d: string | null | undefined) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });
};

const fmtLabel = (s: string) =>
    s
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

const statusColors: Record<string, string> = {
    SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    FAILURE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    PARTIAL: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    DENIED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

// ============================================================================
// Component
// ============================================================================

export const AuditLogsListRoute = () => {
    const [filters, setFilters] = useState<AuditLogsQueryParams>({ page: 0, size: 50 });
    const [showFilters, setShowFilters] = useState(false);
    const [searchInput, setSearchInput] = useState("");

    // ── Queries ──
    const { data, isLoading, error } = useQuery({
        queryKey: ["audit-logs", filters],
        queryFn: () => getAuditLogs(filters),
    });

    const { data: categoriesData } = useQuery({
        queryKey: ["audit-categories"],
        queryFn: getActionCategories,
        staleTime: Infinity,
    });

    const { data: statusesData } = useQuery({
        queryKey: ["audit-statuses"],
        queryFn: getAuditStatuses,
        staleTime: Infinity,
    });

    const logs = data?.data?.logs ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const categories = categoriesData?.data ?? [];
    const statuses = statusesData?.data ?? [];

    const handleSearch = () => {
        setFilters((p) => ({
            ...p,
            search: searchInput.trim() || undefined,
            page: 0,
        }));
    };

    const clearFilters = () => {
        setFilters({ page: 0, size: 50 });
        setSearchInput("");
    };

    const hasActiveFilters =
        filters.adminId ||
        filters.action ||
        filters.category ||
        filters.resourceType ||
        filters.targetUserId ||
        filters.status ||
        filters.startDate ||
        filters.endDate ||
        filters.search;

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-5">
            {/* Search bar + filter toggle */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        placeholder="Search actions, emails, resources…"
                        className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => handleSearch()}>
                    Search
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowFilters((p) => !p)}
                >
                    <FunnelIcon className="mr-1.5 h-4 w-4" />
                    Filters
                </Button>
                {hasActiveFilters && (
                    <Button variant="secondary" size="sm" onClick={clearFilters}>
                        <XMarkIcon className="mr-1 h-4 w-4" /> Clear
                    </Button>
                )}
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
                <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Status */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Status
                        </label>
                        <select
                            value={filters.status ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    status: e.target.value
                                        ? (e.target.value as AuditStatus)
                                        : undefined,
                                    page: 0,
                                }))
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        >
                            <option value="">All Statuses</option>
                            {statuses.map((s) => (
                                <option key={s} value={s}>
                                    {fmtLabel(s)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Category */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Category
                        </label>
                        <select
                            value={filters.category ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    category: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        >
                            <option value="">All Categories</option>
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {fmtLabel(c)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Admin ID */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Admin ID
                        </label>
                        <input
                            type="text"
                            value={filters.adminId ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    adminId: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            placeholder="UUID"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        />
                    </div>

                    {/* Target User ID */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Target User ID
                        </label>
                        <input
                            type="text"
                            value={filters.targetUserId ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    targetUserId: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            placeholder="UUID"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        />
                    </div>

                    {/* Resource Type */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Resource Type
                        </label>
                        <input
                            type="text"
                            value={filters.resourceType ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    resourceType: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            placeholder="e.g. USER, VEHICLE"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        />
                    </div>

                    {/* Action */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Action
                        </label>
                        <input
                            type="text"
                            value={filters.action ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    action: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            placeholder="e.g. SUSPEND_USER"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        />
                    </div>

                    {/* Date Range */}
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={filters.startDate ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    startDate: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={filters.endDate ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({
                                    ...p,
                                    endDate: e.target.value || undefined,
                                    page: 0,
                                }))
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        />
                    </div>
                </div>
            )}

            {/* Loading */}
            {isLoading && (
                <div className="flex items-center justify-center py-20">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                        Failed to load audit logs
                    </p>
                </div>
            )}

            {/* Table */}
            {!isLoading && !error && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                    {logs.length === 0 ? (
                        <div className="py-16 text-center">
                            <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                                No audit logs found
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="border-b border-slate-200 px-4 py-2 dark:border-slate-700">
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {totalElements.toLocaleString()} total entries
                                </p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                Action
                                            </th>
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                Admin
                                            </th>
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                Category
                                            </th>
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                Resource
                                            </th>
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                IP
                                            </th>
                                            <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                                Time
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {logs.map((log) => (
                                            <tr
                                                key={log.id}
                                                className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                            >
                                                <td className="px-4 py-3">
                                                    <Link
                                                        to={`/audit-logs/logs/${log.id}`}
                                                        className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                                    >
                                                        {fmtLabel(log.action)}
                                                    </Link>
                                                    {log.changeSummary && (
                                                        <p className="mt-0.5 max-w-xs truncate text-xs text-slate-400">
                                                            {log.changeSummary}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="text-sm text-slate-900 dark:text-white">
                                                        {log.adminEmail}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        {log.adminRole}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                                    {fmtLabel(log.actionCategory)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs text-slate-600 dark:text-slate-300">
                                                        {log.resourceType}
                                                    </span>
                                                    {log.resourceId && (
                                                        <p className="mt-0.5 max-w-[120px] truncate font-mono text-[10px] text-slate-400">
                                                            {log.resourceId}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[log.status] ??
                                                            statusColors.SUCCESS
                                                            }`}
                                                    >
                                                        {log.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-slate-400">
                                                    {log.ipAddress}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                                    {fmtDate(log.performedAt)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 dark:border-slate-700">
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Page {(filters.page ?? 0) + 1} of {totalPages}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={(filters.page ?? 0) === 0}
                                    onClick={() =>
                                        setFilters((p) => ({ ...p, page: (p.page ?? 0) - 1 }))
                                    }
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={(filters.page ?? 0) >= totalPages - 1}
                                    onClick={() =>
                                        setFilters((p) => ({ ...p, page: (p.page ?? 0) + 1 }))
                                    }
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
